import React from "react";

// Lightweight inline SVG glyphs for each streaming platform card + the share icons.
// Not exact brand logos (avoids trademark path-accuracy issues) — simple, recognizable
// marks tinted by each platform's brand gradient. Zero external icon library dependency.

interface IconProps {
  className?: string;
}

const WaveformIcon: React.FC<IconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M3 12h1.5M7 8v8M11 5v14M15 8v8M19 10v4M21.5 12H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const MusicNoteIcon: React.FC<IconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M9 18V5l11-2v13" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="17" cy="16" r="3" />
  </svg>
);

const PlayCircleIcon: React.FC<IconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10 8.5l6 3.5-6 3.5v-7z" fill="currentColor" />
  </svg>
);

const HeadphonesIcon: React.FC<IconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M4 14v-2a8 8 0 0116 0v2M4 14a2 2 0 002 2h1a1 1 0 001-1v-3a1 1 0 00-1-1H5a1 1 0 00-1 1v2zm16 0a2 2 0 01-2 2h-1a1 1 0 01-1-1v-3a1 1 0 011-1h1a1 1 0 011 1v2z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PLATFORM_ICON: Record<string, React.FC<IconProps>> = {
  spotify: WaveformIcon,
  youtubeMusic: PlayCircleIcon,
  appleMusic: MusicNoteIcon,
  amazonMusic: HeadphonesIcon,
  jioSaavn: MusicNoteIcon,
  gaana: WaveformIcon,
};

export const PlatformIcon: React.FC<{ id: string; className?: string }> = ({ id, className = "w-6 h-6" }) => {
  const Icon = PLATFORM_ICON[id] || MusicNoteIcon;
  return <Icon className={className} />;
};

export const WhatsAppIcon: React.FC<IconProps> = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2a10 10 0 00-8.5 15.2L2 22l4.9-1.5A10 10 0 1012 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.3-.5.1-1 .1-1.7-.1-.4-.1-.9-.3-1.5-.6-2.6-1.1-4.3-3.7-4.4-3.9-.1-.2-1-1.3-1-2.5s.6-1.8.9-2.1c.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .5.4.2.5.7 1.7.7 1.9.1.1.1.3 0 .4-.1.2-.1.3-.3.4-.1.2-.3.3-.4.5-.1.1-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.4 2.4 1.5.3.1.4.1.6-.1.2-.2.7-.8.9-1.1.2-.3.4-.2.6-.1l1.7.8c.2.1.4.2.4.3.1.2.1.7-.1 1.2z" />
  </svg>
);

export const FacebookIcon: React.FC<IconProps> = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V8c0-.9.3-1.5 1.6-1.5H17V3.6C16.6 3.5 15.7 3.4 14.6 3.4c-2.3 0-3.9 1.4-3.9 4v2.5H8v3.1h2.7v8h2.8z" />
  </svg>
);

export const XIcon: React.FC<IconProps> = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.9 3H22l-7.3 8.3L23 21h-6.6l-5.2-6.6L5.2 21H2l7.8-8.9L1.5 3h6.7l4.7 6.1L18.9 3zm-1.2 16h1.7L7.4 5H5.6l12.1 14z" />
  </svg>
);

export const CopyLinkIcon: React.FC<IconProps> = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M9 12a4 4 0 004 4h1a4 4 0 000-8h-1M15 12a4 4 0 00-4-4H10a4 4 0 000 8h1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const InstagramIcon: React.FC<IconProps> = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" />
  </svg>
);

export const PlayFilledIcon: React.FC<IconProps> = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M8 5.5v13l11-6.5-11-6.5z" />
  </svg>
);
